/**
 * Presentational pieces of the processing console shell: the validation feed,
 * the card wrapper, the brand mark and the keyframes Tailwind does not ship.
 *
 * The feed renders findings with STATUS_CONFIG imported from
 * ValidationResultPanel, so a Pass/Warn/Fail here looks identical to the same
 * finding on the results panel.
 */
import React from "react";
import { AlertTriangle, ShieldCheck } from "lucide-react";
import type {
  ValidateFileResult,
  ValidateFileSummary,
  ValidateRuleResult,
} from "../../../api/frameworksApi";
import {
  STATUS_CONFIG,
  CATEGORY_META,
} from "../../components/ValidationResultPanel";
import {
  PIPELINE_STAGES,
  type ConsolePhase,
  type PipelineRun,
} from "./processingPipeline";

/* ── Validation feed ──
   Findings are grouped by semantic category (metadata, structural, semantic).
   The API returns `resultsByCategory` and `summaryByCategory` already grouped,
   so we render them as sections with sticky headers instead of the old flat
   list - the same underlying findings, but scannable by category. */

/** Known categories, rendered in this order. Anything else is appended after. */
const CATEGORY_ORDER = ["metadata", "structural", "semantic"] as const;

/** Category ordering: known categories first (in fixed order), unknown at end. */
function sortedCategoryKeys(
  byCat: Record<string, ValidateRuleResult[]>,
): string[] {
  const known = CATEGORY_ORDER.filter((c) => (byCat[c]?.length ?? 0) > 0);
  const extra = Object.keys(byCat)
    .filter(
      (c) =>
        !CATEGORY_ORDER.includes(c as (typeof CATEGORY_ORDER)[number]) &&
        (byCat[c]?.length ?? 0) > 0,
    )
    .sort();
  return [...known, ...extra];
}

/** Header tone reflects the worst outcome in the category. */
function categoryTone(summary: ValidateFileSummary): {
  dot: string;
  border: string;
  bg: string;
} {
  if ((summary.fail ?? 0) > 0)
    return {
      dot: "bg-red-500",
      border: "border-red-200/60 dark:border-red-500/20",
      bg: "bg-red-50/60 dark:bg-red-500/[0.05]",
    };
  if ((summary.warn ?? 0) > 0)
    return {
      dot: "bg-amber-500",
      border: "border-amber-200/60 dark:border-amber-500/20",
      bg: "bg-amber-50/60 dark:bg-amber-500/[0.05]",
    };
  if ((summary.pass ?? 0) > 0)
    return {
      dot: "bg-emerald-500",
      border: "border-emerald-200/60 dark:border-emerald-500/20",
      bg: "bg-emerald-50/60 dark:bg-emerald-500/[0.05]",
    };
  return {
    dot: "bg-slate-400",
    border: "border-border",
    bg: "bg-[#f8fafc] dark:bg-white/[0.02]",
  };
}

/** Short "3 passed · 2 failed · 1 warning" summary line for a category. */
function summaryText(summary: ValidateFileSummary): string {
  const parts: string[] = [];
  if (summary.pass) parts.push(`${summary.pass} passed`);
  if (summary.fail) parts.push(`${summary.fail} failed`);
  if (summary.warn)
    parts.push(`${summary.warn} warning${summary.warn === 1 ? "" : "s"}`);
  if (summary.skipped) parts.push(`${summary.skipped} skipped`);
  return parts.join(" · ") || `${summary.total} total`;
}

/** Status filter values for the feed's chip row. */
type FeedStatusFilter = "ALL" | "PASS" | "FAIL" | "WARN" | "SKIPPED";

/**
 * Chip config for the filter row. `status` is `null` for the "All" chip
 * (which matches every row) and one of `ValidateRuleStatus` for the rest.
 */
const STATUS_FILTER_CHIPS: {
  value: FeedStatusFilter;
  label: string;
  /** Optional tone applied when the chip is selected. */
  activeCls: string;
}[] = [
  {
    value: "ALL",
    label: "All",
    activeCls:
      "border-indigo-300 bg-indigo-50 text-indigo-700 dark:border-indigo-500/40 dark:bg-indigo-500/10 dark:text-indigo-300",
  },
  {
    value: "PASS",
    label: "Passed",
    activeCls:
      "border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-300",
  },
  {
    value: "FAIL",
    label: "Failed",
    activeCls:
      "border-red-300 bg-red-50 text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300",
  },
  {
    value: "WARN",
    label: "Warnings",
    activeCls:
      "border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300",
  },
  {
    value: "SKIPPED",
    label: "Skipped",
    activeCls:
      "border-slate-300 bg-slate-100 text-slate-700 dark:border-white/20 dark:bg-white/[0.06] dark:text-white/70",
  },
];

/** Total count of rules matching a given filter, across all categories. */
function countForFilter(
  summary: ValidateFileSummary,
  filter: FeedStatusFilter,
): number {
  switch (filter) {
    case "ALL":
      return summary.total;
    case "PASS":
      return summary.pass;
    case "FAIL":
      return summary.fail;
    case "WARN":
      return summary.warn;
    case "SKIPPED":
      return summary.skipped;
  }
}

export function ExtractionFeed({
  result,
  run,
  phase,
  errorMessage,
}: {
  result: ValidateFileResult | null;
  run: PipelineRun;
  phase: ConsolePhase;
  errorMessage?: string | null;
}) {
  const [statusFilter, setStatusFilter] =
    React.useState<FeedStatusFilter>("ALL");

  if (phase === "error") {
    return (
      <div className="flex flex-1 items-start gap-2.5 px-4 pb-4">
        <AlertTriangle className="mt-0.5 size-4 flex-none text-red-500" />
        <p className="break-words text-xs leading-snug text-muted-foreground">
          {errorMessage ?? "Validation could not be completed."}
        </p>
      </div>
    );
  }

  if (result) {
    const cats = sortedCategoryKeys(result.resultsByCategory);
    if (cats.length === 0) {
      return (
        <div className="flex flex-1 items-start gap-2.5 px-4 pb-4">
          <p className="break-words text-xs leading-snug text-muted-foreground">
            Validation completed but returned no rule results.
          </p>
        </div>
      );
    }
    // Global counts for the filter chip labels.
    const overall = result.summary;
    // Categories that still have rows after the current filter is applied.
    const visibleCats = cats.filter((cat) => {
      const rules = result.resultsByCategory[cat] ?? [];
      return (
        statusFilter === "ALL" ||
        rules.some((r) => r.status === statusFilter)
      );
    });
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-2 px-2.5 pb-3 lg:overflow-y-auto">
        {/* Status filter chips - stick to the top of the scroll area so
            the user can flip between "All / Passed / Failed / Warnings /
            Skipped" without scrolling back up. Category headers below
            sit at top-[34px] to stay under this row when they stick. */}
        <div className="sticky top-0 z-[2] -mx-2.5 flex flex-wrap items-center gap-1 border-b border-border bg-background/95 px-2.5 py-1.5 backdrop-blur">
          {STATUS_FILTER_CHIPS.map((chip) => {
            const count = countForFilter(overall, chip.value);
            const active = statusFilter === chip.value;
            return (
              <button
                key={chip.value}
                type="button"
                onClick={() => setStatusFilter(chip.value)}
                className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-colors ${
                  active
                    ? chip.activeCls
                    : "border-border bg-transparent text-muted-foreground hover:bg-accent"
                }`}
              >
                {chip.label}
                <span
                  className={`tabular-nums text-[9.5px] font-medium ${
                    active ? "opacity-80" : "opacity-60"
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
        {visibleCats.length === 0 && (
          <div className="flex flex-1 items-start gap-2.5 px-2 pt-2">
            <p className="break-words text-xs leading-snug text-muted-foreground">
              No rules match this filter.
            </p>
          </div>
        )}
        {visibleCats.map((cat) => {
          const allRules = result.resultsByCategory[cat] ?? [];
          const rules =
            statusFilter === "ALL"
              ? allRules
              : allRules.filter((r) => r.status === statusFilter);
          const summary = result.summaryByCategory[cat] ?? {
            pass: 0,
            fail: 0,
            warn: 0,
            skipped: 0,
            total: allRules.length,
          };
          const meta = CATEGORY_META[cat];

          const label =
            meta?.label ?? (cat ? cat.charAt(0).toUpperCase() + cat.slice(1) : cat);
          const tone = categoryTone(summary);
          return (
            <section key={cat} className="flex flex-col gap-1">
              <div
                className={`sticky top-[34px] z-[1] flex flex-wrap items-center gap-1.5 rounded-md border px-2 py-1 backdrop-blur-sm ${tone.border} ${tone.bg}`}
              >
                <span
                  className={`size-1.5 flex-none rounded-full ${tone.dot}`}
                />
                <span className="text-[10.5px] font-bold tracking-wide text-foreground">
                  {label}
                </span>
                <span className="ml-auto flex-none whitespace-nowrap text-[9.5px] text-muted-foreground">
                  {summaryText(summary)} · {summary.total} total
                </span>
              </div>
              {rules.map((r, i) => {
                const s =
                  STATUS_CONFIG[r.status as keyof typeof STATUS_CONFIG] ??
                  STATUS_CONFIG.SKIPPED;
                const Icon = s.icon;
                const detail =
                  r.status === "SKIPPED" ? r.skipReason : r.remediation;
                return (
                  <div
                    key={`${r.ruleId}-${i}`}
                    className={`rounded-md border border-border px-2 py-1.5 ${s.rowBg || "bg-[#f8fafc] dark:bg-white/[0.02]"}`}
                    style={{
                      animation: "rgc-slidein .4s cubic-bezier(.2,.8,.2,1)",
                    }}
                  >
                    <div className="mb-0.5 flex items-center gap-1.5">
                      <Icon className={`size-3 flex-none ${s.iconCls}`} />
                      <span
                        className={`flex-none whitespace-nowrap rounded border px-1 py-0.5 text-[9.5px] font-bold ${s.pillCls}`}
                      >
                        {s.label}
                      </span>
                      {r.ruleId && (
                        <span className="ml-auto flex-none font-mono text-[9.5px] font-medium text-muted-foreground">
                          {r.ruleId}
                        </span>
                      )}
                    </div>
                    <div
                      className="break-words text-[11.5px] leading-snug text-foreground"
                      title={r.name}
                    >
                      {r.name}
                    </div>
                    {detail && (
                      <div
                        className="mt-0.5 line-clamp-2 break-words text-[10px] leading-snug text-muted-foreground"
                        title={detail}
                      >
                        {detail}
                      </div>
                    )}
                  </div>
                );
              })}
            </section>
          );
        })}
      </div>
    );
  }

  // Nothing real to show yet - narrate the stage actually running.
  // Font sizes are one notch smaller than the settled feed so this
  // interim "what's happening now" list doesn't dominate the sidebar
  // before real rule cards start landing.
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-1.5 px-3 pb-3 lg:overflow-y-auto">
      {PIPELINE_STAGES.slice(0, run.stage + 1)
        .reverse()
        .map((s, idx) => (
          <div
            key={s.key}
            className={`rounded-lg border px-2.5 py-1.5 ${
              idx === 0
                ? "border-indigo-200 bg-indigo-50/60 dark:border-indigo-500/30 dark:bg-indigo-500/[0.06]"
                : "border-border bg-[#f8fafc] dark:bg-white/[0.02]"
            }`}
            style={{ animation: "rgc-slidein .4s cubic-bezier(.2,.8,.2,1)" }}
          >
            <div
              className={`text-[11px] font-semibold ${
                idx === 0
                  ? "text-indigo-600 dark:text-indigo-400"
                  : "text-muted-foreground"
              }`}
            >
              {s.label}
            </div>
            <div className="mt-0.5 break-words text-[10px] leading-snug text-muted-foreground">
              {s.hint}
            </div>
          </div>
        ))}
    </div>
  );
}

/* ── Small building blocks ── */
export function Panel({
  title,
  meta,
  children,
  grow,
  noHeader,
}: {
  title?: string;
  meta?: string;
  children: React.ReactNode;
  grow?: boolean;
  noHeader?: boolean;
}) {
  return (
    <section
      className={`relative flex min-h-0 flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm ${
        grow ? "flex-1" : ""
      }`}
    >
      {!noHeader && (
        <>
          <div className="flex items-center justify-between gap-2 px-4 pb-2.5 pt-3">
            <span className="flex items-center gap-2 text-[11px] font-semibold tracking-wide text-muted-foreground">
              <span className="size-1.5 flex-none rounded-full bg-indigo-500" />
              {title}
            </span>
            {meta && (
              <span className="flex-none text-[11px] font-medium text-muted-foreground">
                {meta}
              </span>
            )}
          </div>
          <div className="h-px bg-border" />
        </>
      )}
      {children}
    </section>
  );
}

export function ConsoleLogo() {
  return (
    <span className="grid size-9 flex-none place-items-center rounded-xl bg-indigo-50 text-indigo-600 ring-1 ring-indigo-200/60 dark:bg-indigo-500/10 dark:text-indigo-400 dark:ring-indigo-500/20">
      <ShieldCheck className="size-5" />
    </span>
  );
}

/** Keyframes the console needs that Tailwind does not ship. */
export function ConsoleKeyframes() {
  return (
    <style>{`
@keyframes rgc-blink { 50% { opacity: .4 } }
@keyframes rgc-ring { 0% { transform: scale(1); opacity: .7 } 100% { transform: scale(1.7); opacity: 0 } }
@keyframes rgc-slidein { from { opacity: 0; transform: translateX(14px) } to { opacity: 1; transform: translateX(0) } }
/* Scale only - Tailwind v4 centres the core with the standalone \`translate\`
   property, which composes with \`transform\`. Translating here too would
   double-shift the element. */
@keyframes rgc-breathe { 0%,100% { transform: scale(1) } 50% { transform: scale(1.06) } }
/* Shockwaves radiating off the core. */
@keyframes rgc-pulse-out {
  0%   { transform: scale(.9);  opacity: .5 }
  70%  { opacity: .12 }
  100% { transform: scale(2.1); opacity: 0 }
}
/* Slow rotation for the conic halo and the dashed orbit guide. */
@keyframes rgc-spin { to { transform: rotate(360deg) } }
@keyframes rgc-spin-rev { to { transform: rotate(-360deg) } }
/* Data travelling out along a spoke to its layer. */
@keyframes rgc-travel {
  0%   { transform: translateX(0) translateY(-50%); opacity: 0 }
  12%  { opacity: 1 }
  80%  { opacity: 1 }
  100% { transform: translateX(var(--rgc-reach, 120px)) translateY(-50%); opacity: 0 }
}
/* Gentle bob so the ring never feels frozen. */
@keyframes rgc-drift { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-5px) } }
/* Highlight sweeping across the core's surface. */
@keyframes rgc-shimmer { 0%,100% { opacity: .55 } 50% { opacity: .9 } }
/* Failure attention popup above the halted pipeline stage. */
@keyframes rgc-fail-pop {
  0%   { opacity: 0; transform: translate(-50%, 6px) scale(.85) }
  55%  { opacity: 1; transform: translate(-50%, 0) scale(1.06) }
  100% { opacity: 1; transform: translate(-50%, 0) scale(1) }
}
@keyframes rgc-fail-out {
  0%   { opacity: 1; transform: translate(-50%, 0) scale(1) }
  100% { opacity: 0; transform: translate(-50%, -4px) scale(.96) }
}
/* Persistent "something broke" cues: the halted stage shudders every few
   seconds, and the spark sitting in the severed connector's gap jitters
   like a loose wire. Both are muted so they nag without pounding. */
@keyframes rgc-fail-shake {
  0%, 92%, 100% { transform: translateX(0) }
  93% { transform: translateX(-1.5px) }
  94% { transform: translateX(1.5px) }
  95% { transform: translateX(-1.5px) }
  96% { transform: translateX(1.5px) }
  97% { transform: translateX(-1px) }
  98% { transform: translateX(1px) }
}
@keyframes rgc-broken-jitter {
  0%, 100% { transform: scale(1); opacity: .85 }
  50% { transform: scale(1.35); opacity: 1 }
}
@media (prefers-reduced-motion: reduce) {
  [style*="rgc-"] { animation-duration: .001ms !important; animation-iteration-count: 1 !important }
}
`}</style>
  );
}
